// Third-party
import React, { useEffect, useState } from 'react';

// Components
import ExportPresentationModalV1 from './ExportPresentationModalV1';
import ExportPresentationModalV2 from './ExportPresentationModalV2';

// Types
import Locale from '@/types/locale';
import Presentation from '@/types/presentation';
import User from '@/types/user';

// Services
import { getPresentations } from '../../../../services/omni';
import { addUsageLog } from '@/services/usage-log/usage-log-new';

// Utils
import { USAGE_TYPES } from '@/utils/usage-log';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';

type Props = {
  user: User;
  comparisonId: string;
  show: boolean;
  presentationTemplates: string[];
  locale: Locale;
  onClose: () => void;
  onCreatePDFProposal: () => void;
  onCreateOmniProposal: () => void;
  canCreateOmniProposal: {
    isDirty?: boolean;
    hasContact?: boolean;
  };
};

const ExportPresentationModal = ({
  user,
  comparisonId,
  show,
  presentationTemplates,
  locale,
  onClose,
  onCreatePDFProposal,
  onCreateOmniProposal,
  canCreateOmniProposal,
}: Props) => {
  const [presentationsHistory, setPresentationsHistory] = useState<Presentation[] | undefined | null>(undefined);
  const showExportModalV2 = userHasPermissionEnabled(user, PERMISSIONS.OMNI_EXPORT_MODAL_REWORK);

  const disableOmni = !presentationTemplates?.length;

  useEffect(() => {
    addUsageLog(USAGE_TYPES.OPEN_EXPORT_PRESENTATION_MODAL, { pdf: true, online: !disableOmni });
  }, [show]);

  useEffect(() => {
    const fetchPresentations = async () => {
      try {
        const presentations = await getPresentations(comparisonId);
        setPresentationsHistory(presentations.data);
      } catch (err) {
        setPresentationsHistory(null);
      }
    };

    if (canCreateOmniProposal.isDirty || !canCreateOmniProposal.hasContact) {
      setPresentationsHistory([]);
    } else {
      fetchPresentations();
    }
  }, [canCreateOmniProposal.hasContact, canCreateOmniProposal.isDirty, comparisonId]);

  const handleOnCreatePDFProposal = () => {
    onCreatePDFProposal();
    onClose();
  };

  const handleOnCreateOmniProposal = () => {
    onCreateOmniProposal();
    onClose();
  };

  if (showExportModalV2)
    return (
      <ExportPresentationModalV2
        handleOnCreatePDFProposal={handleOnCreatePDFProposal}
        handleOnCreateOmniProposal={handleOnCreateOmniProposal}
        presentationsHistory={presentationsHistory}
        disableOmni={disableOmni}
        canCreateOmniProposal={canCreateOmniProposal}
        presentationTemplates={presentationTemplates}
        locale={locale}
        show={show}
        onClose={onClose}
      />
    );

  return (
    <ExportPresentationModalV1
      handleOnCreatePDFProposal={handleOnCreatePDFProposal}
      handleOnCreateOmniProposal={handleOnCreateOmniProposal}
      presentationsHistory={presentationsHistory}
      disableOmni={disableOmni}
      canCreateOmniProposal={canCreateOmniProposal}
      presentationTemplates={presentationTemplates}
      locale={locale}
      show={show}
      onClose={onClose}
    />
  );
};

export default ExportPresentationModal;
